import type { ProductFilters } from '#root/api/clients/product/data-contracts'

/**
 * Shape of SEO indexable facets returned by the catalog API.
 */
export interface IndexableFacets {
  [facetLabel: string]: string[]
}

/**
 * Plain crawlable facet link definition derived from `seo.indexableFacets` of Catalog API response.
 */
export interface IndexableFacetLink {
  facetLabel: string
  href: string
  text: string
  value: string
}

const getFacetDisplayValue = (facetValue: string) => facetValue.split(';')[0]
const getComparableFacetValue = (facetValue: string) => {
  try {
    return decodeURIComponent(facetValue).replace(/%3B/gi, ';')
  }
  catch {
    return facetValue.replace(/%3B/gi, ';')
  }
}

/**
 * Determines whether the selected filters represent one API-approved indexable facet URL.
 *
 * @param selectedFilters Current filter selections keyed by API filter code.
 * @param availableFilters Filters returned by the catalog API.
 * @param indexableFacets Indexable facet labels and values returned in the catalog SEO payload.
 * @returns Whether the selection is exactly one indexable facet value.
 * @category Utils
 */
export function isIndexableFacetSelection(
  selectedFilters: Record<string, string[]>,
  availableFilters: ProductFilters,
  indexableFacets?: IndexableFacets | null,
): boolean {
  if (!indexableFacets) return false

  const activeSelections = Object.entries(selectedFilters)
    .filter(([, selectedValues]) => selectedValues.length)

  if (activeSelections.length !== 1) return false

  const [[selectedCode, selectedValues]] = activeSelections
  if (selectedValues.length !== 1) return false

  const selectedFilter = availableFilters.find(({ code }) => code === selectedCode)
  const indexableValues = Object.entries(indexableFacets).find(([facetLabel]) =>
    [selectedCode, selectedFilter?.code, selectedFilter?.label]
      .filter((value): value is string => Boolean(value))
      .some((value) => facetLabel.toLowerCase() === value.toLowerCase())
  )?.[1]

  return !!indexableValues?.some((facetValue) =>
    getComparableFacetValue(facetValue) === getComparableFacetValue(selectedValues[0])
  )
}

/**
 * Builds plain anchor-link models for SEO indexable facets using existing
 * `filters` query-param URL contract.
 *
 * Each link represents a single faceted PLP URL and can be rendered as a genuine
 * HTML anchor element so crawlers can discover facet pages without JavaScript
 * interaction.
 *
 * @param category Current PLP category name.
 * @param path Current PLP path.
 * @param indexableFacets Raw `seo.indexableFacets` payload from the API.
 * @returns Flat list of crawlable facet links.
 * @category Utils
 */
export default function getIndexableFacetLinks(
  category: string,
  path: string,
  indexableFacets?: IndexableFacets | null,
): IndexableFacetLink[] {
  if (!path || !indexableFacets) return []

  return Object.entries(indexableFacets).flatMap(([facetLabel, facetValues]) => facetValues.map((facetValue) => {
    const displayValue = getFacetDisplayValue(facetValue)
    const query = {
      filters: JSON.stringify({
        [facetLabel]: [facetValue]
      }),
      indexable: 'true'
    }
    return {
      facetLabel,
      href: `${path}?${(new URLSearchParams(query)).toString()}`,
      text: `${displayValue} ${category}`,
      value: facetValue,
    }
  }))
}
